import React, { useEffect, useState, useCallback, useRef } from "react";
import { SkeletonLine } from "../utils/Skeleton";
import OrdersRow from "./TradeTableComponents/OrdersRow";
import TradesRow from "./TradeTableComponents/TradesRow";
import { useSelector } from "react-redux";
import { getTradesWithInfos } from "../functions/Contract";
import { fetchOpenTradesPnl, parseTradeIndex } from "../functions/graphqlPnl";
import HistoryRow from "./TradeTableComponents/HistoryRow";
import { useWalletConnection } from "../../hooks/useWalletConnection";
import { useNavigate, useLocation } from "react-router-dom";
import { navigateToTradeMarket, getTradeIndexNumber } from "../../utils/tradeNavigation";
import { getMarketName } from "../../utils/marketUtils";
import { toast } from "react-toastify";
import { PurpleButton } from "../Buttons/PurpleButton";
import {
  TRADES_GRID_COLUMNS,
  TRADES_MIN_WIDTH,
  TRADES_ROW_PADDING_X,
} from "./TradeTableComponents/tableLayout";
import Modal from "react-responsive-modal";
import DateField from "../DatePicker/DateField";
import "react-responsive-modal/styles.css";

const AllTradesTable = ({ onOpenTradesChange }) => {
  const [list, setList] = useState(0); // 0: Open, 1: Orders, 2: Closed
  const [trades, setTrades] = useState([]);
  const [filteredTrades, setFilteredTrades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [highlightedTradeId, setHighlightedTradeId] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pnlFilter, setPnlFilter] = useState('all'); // 'all', 'profit', 'loss'
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Sorting is deliberately limited to fields that live on the trade object.
  // PnL for an open position is derived from a live price inside the row, so it
  // is not knowable here and cannot be sorted on without duplicating that math.
  const [sortKey, setSortKey] = useState('date'); // 'date' | 'size'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const tradeRowRefs = useRef({});
  const highlightTimeoutRef = useRef(null);
  
  const account = useSelector((state) => state.auth.account);
  const bech32Address = useSelector((state) => state.auth.bech32Address);
  const perpsUpdate = useSelector((state) => state.trade.update);
  const { walletType } = useWalletConnection();
  const navigate = useNavigate();
  const location = useLocation();
  const prevPerpsUpdateRef = useRef(undefined);

  const getTrades = useCallback(async (force = false) => {
    if (!account) {
      setTrades([]);
      setFilteredTrades([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('📊 Loading all trades across all markets');
      
      // Get all trades from all markets (open fresh; closed from cache unless forced),
      // plus one bulk request for open trades' authoritative on-chain PnL — the
      // same source TradeDetailModal uses, so the table row and the detail modal
      // agree instead of the row's own fee-blind price-tick estimate drifting
      // from the contract's actual number. One request per refresh, not per row.
      const [allTrades, onchainPnlById] = await Promise.all([
        getTradesWithInfos(account, walletType, { forceClosed: force }),
        fetchOpenTradesPnl(bech32Address),
      ]);

      if (!allTrades || allTrades.length === 0) {
        setTrades([]);
        setFilteredTrades([]);
        setIsLoading(false);
        return;
      }

      if (onchainPnlById.size > 0) {
        for (const trade of allTrades) {
          if (!trade.is_open) continue;
          const idx = parseTradeIndex(trade.user_trade_index);
          if (idx != null && onchainPnlById.has(idx)) {
            trade.onchainPnl = onchainPnlById.get(idx);
          }
        }
      }

      // Sort by timestamp (newest first)
      const sortedTrades = allTrades.sort((a, b) => {
        const timestampA = a.trade_info?.last_oi_update_ts || a.open_timestamp || 0;
        const timestampB = b.trade_info?.last_oi_update_ts || b.open_timestamp || 0;
        return Number(timestampB) - Number(timestampA);
      });

      console.log(`✅ Loaded ${sortedTrades.length} trades across all markets`);
      setTrades(sortedTrades);
    } catch (error) {
      console.error("Error loading all trades:", error);
      setTrades([]);
      setFilteredTrades([]);
    } finally {
      setIsLoading(false);
    }
  }, [account, walletType, bech32Address]);

  // Helper function to normalize timestamp to milliseconds
  const normalizeTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    let numTimestamp;
    if (typeof timestamp === 'string') {
      numTimestamp = parseInt(timestamp.trim());
      if (isNaN(numTimestamp)) return null;
    } else if (typeof timestamp === 'number') {
      numTimestamp = timestamp;
    } else {
      return null;
    }

    // Handle nanosecond timestamps (19 digits) - convert to milliseconds
    if (numTimestamp.toString().length >= 19) {
      return Math.floor(numTimestamp / 1000000);
    } else if (numTimestamp > 1e12) {
      // Already in milliseconds
      return numTimestamp;
    } else {
      // Seconds - multiply by 1000 to get milliseconds
      return numTimestamp * 1000;
    }
  };

  // Helper function to convert date string (YYYY-MM-DD) to timestamp at start of day
  const dateToStartTimestamp = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString + 'T00:00:00');
    return date.getTime();
  };

  // Helper function to convert date string (YYYY-MM-DD) to timestamp at end of day
  const dateToEndTimestamp = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString + 'T23:59:59.999');
    return date.getTime();
  };

  // Helper function to determine if a trade is profitable
  const isTradeProfitable = (trade) => {
    const openPrice = parseFloat(trade.open_price);
    if (!openPrice || isNaN(openPrice)) return null; // Can't determine

    // For closed trades, use close price
    if (!trade.is_open) {
      const closePrice = parseFloat(trade.trade_info?.closed_price || trade.close_price);
      if (!closePrice || isNaN(closePrice)) return null;

      if (trade.long) {
        // Long: profit if close > open
        return closePrice > openPrice;
      } else {
        // Short: profit if close < open
        return closePrice < openPrice;
      }
    }
    return null;
  };

  // Filter trades based on selected filter and date range
  useEffect(() => {
    if (trades.length === 0) {
      setFilteredTrades([]);
      return;
    }

    let filtered = [];
    switch (list) {
      case 0: // Open Trades (live positions only)
        filtered = trades.filter(trade => trade.is_open && trade.trade_type === 'trade');
        break;
      case 1: // Pending Orders (conditional: limit + stop)
        filtered = trades.filter(trade => trade.is_open && (trade.trade_type === 'limit' || trade.trade_type === 'stop'));
        break;
      case 2: // Closed Trades
        filtered = trades.filter(trade => !trade.is_open);
        break;
      default:
        filtered = [];
        break;
    }

    // Apply date range filtering
    if (fromDate || toDate) {
      const fromTimestamp = fromDate ? dateToStartTimestamp(fromDate) : null;
      const toTimestamp = toDate ? dateToEndTimestamp(toDate) : null;

      filtered = filtered.filter(trade => {
        const tradeTimestamp = normalizeTimestamp(
          trade.trade_info?.last_oi_update_ts || trade.open_timestamp
        );
        
        if (!tradeTimestamp) return false;

        if (fromTimestamp && tradeTimestamp < fromTimestamp) {
          return false;
        }
        
        if (toTimestamp && tradeTimestamp > toTimestamp) {
          return false;
        }

        return true;
      });
    }

    // Apply profit/loss filtering
    if (pnlFilter !== 'all') {
      filtered = filtered.filter(trade => {
        const isProfit = isTradeProfitable(trade);
        if (isProfit === null) {
          // If we can't determine (e.g., open trades without current price), exclude from profit/loss filters
          return false;
        }
        return pnlFilter === 'profit' ? isProfit : !isProfit;
      });
    }

    // Apply free-text search — market name or trade index, whichever the user
    // happens to remember about the position they are looking for.
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(trade => {
        const market = (getMarketName(trade.market_index) || '').toLowerCase();
        const index = (getTradeIndexNumber(trade.user_trade_index) ?? '').toString();
        const side = trade.long ? 'long buy' : 'short sell';
        return market.includes(term) || index.includes(term) || side.includes(term);
      });
    }

    // Apply sort
    const sizeOf = (t) =>
      (parseFloat(t.collateral_amount) || 0) * (parseFloat(t.leverage) || 0);
    const dateOf = (t) =>
      Number(normalizeTimestamp(t.trade_info?.last_oi_update_ts || t.open_timestamp)) || 0;

    const read = sortKey === 'size' ? sizeOf : dateOf;
    filtered = [...filtered].sort((a, b) =>
      sortDir === 'asc' ? read(a) - read(b) : read(b) - read(a)
    );

    setFilteredTrades(filtered);
  }, [trades, list, fromDate, toDate, pnlFilter, search, sortKey, sortDir]);

  // Notify parent component about open trades for TotalPnL calculation
  useEffect(() => {
    if (onOpenTradesChange) {
      const openTrades = trades.filter(trade => trade.is_open && trade.trade_type === 'trade');
      onOpenTradesChange(openTrades);
    }
  }, [trades, onOpenTradesChange]);

  useEffect(() => {
    // Force a closed-history refresh only when a trade action occurred (perpsUpdate
    // bumped) AFTER mount. The initial mount (ref still undefined) reuses the cache
    // so opening "My Trades" is instant instead of re-scanning all markets.
    const prev = prevPerpsUpdateRef.current;
    const actionHappened = prev !== undefined && prev !== perpsUpdate;
    prevPerpsUpdateRef.current = perpsUpdate;
    getTrades(actionHappened);
  }, [account, perpsUpdate, getTrades]);

  // Reconcile after a background revalidation of a reload-hydrated cache.
  useEffect(() => {
    const onRevalidated = () => getTrades(false);
    window.addEventListener("tradesRevalidated", onRevalidated);
    return () => window.removeEventListener("tradesRevalidated", onRevalidated);
  }, [getTrades]);

  // Listen for trade close events and refresh trades
  useEffect(() => {
    let refreshTimeout = null;

    const handleTradeClosed = async (event) => {
      const { action, tradeIndex } = event.detail || {};
      
      // Only handle close_trade actions
      if (action === 'close_trade') {
        console.log('🔄 Trade closed, will refresh trades list...', { tradeIndex });
        
        // Clear any pending refresh
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        
        // Refresh trades after a delay to allow blockchain state to settle
        // This ensures the closed trade is removed from the Open tab
        refreshTimeout = setTimeout(() => {
          console.log('✅ Refreshing trades after close trade');
          getTrades();
        }, 2000);
        
        // If we're on the Open tab, ensure it stays visible (trade will be removed from list)
        // The filtered trades will automatically update when getTrades completes
        if (list === 0) {
          console.log('✅ On Open tab - closed trade will be removed from view after refresh');
        }
      }
    };

    // Listen for user trade actions (including close trade)
    window.addEventListener('userTradeAction', handleTradeClosed);
    
    // Also listen for blockchain state checks which may indicate trades were closed
    // This provides a secondary refresh after blockchain confirms the state change
    const handleBlockchainStateCheck = () => {
      // Clear any pending refresh from userTradeAction
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      
      // Refresh trades when blockchain state changes (after confirmation)
      refreshTimeout = setTimeout(() => {
        console.log('✅ Refreshing trades after blockchain state check');
        getTrades();
      }, 1500);
    };
    window.addEventListener('blockchainStateCheck', handleBlockchainStateCheck);

    return () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      window.removeEventListener('userTradeAction', handleTradeClosed);
      window.removeEventListener('blockchainStateCheck', handleBlockchainStateCheck);
    };
  }, [getTrades, list]);

  // Auto-switch to Closed trades tab when profit/loss filter is selected
  useEffect(() => {
    if (pnlFilter === 'profit' || pnlFilter === 'loss') {
      setList(2); // Switch to Closed trades tab
    }
  }, [pnlFilter]);

  // Handle highlighting from URL query param
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const highlightTrade = queryParams.get('highlightTrade');

    if (highlightTrade && filteredTrades.length > 0) {
      const tradeToHighlight = filteredTrades.find(trade => {
        const tradeIndex = getTradeIndexNumber(trade.user_trade_index);
        return tradeIndex && tradeIndex.toString() === highlightTrade.toString();
      });

      if (tradeToHighlight) {
        // Determine the correct list to switch to
        let targetList = 0; // Default to Open
        if ((tradeToHighlight.trade_type === 'limit' || tradeToHighlight.trade_type === 'stop') && tradeToHighlight.is_open) {
          targetList = 1; // Orders
        } else if (!tradeToHighlight.is_open) {
          targetList = 2; // Closed
        }
        setList(targetList); // Switch tab

        setHighlightedTradeId(tradeToHighlight.user_trade_index);
        
        // Scroll to the trade row after a short delay to ensure it's rendered
        setTimeout(() => {
          const rowElement = tradeRowRefs.current[tradeToHighlight.user_trade_index];
          if (rowElement) {
            rowElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 300);

        // Remove highlight after 5 seconds
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedTradeId(null);
          // Clear the query param from URL
          navigate(location.pathname, { replace: true });
        }, 5000);
      }
    }

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, [location.search, filteredTrades, navigate, location.pathname]);

  // Handle trade click - navigate to market page
  const handleTradeClick = useCallback((trade) => {
    navigateToTradeMarket(navigate, trade);
  }, [navigate]);

  // Manual refresh — forces a closed-history rescan rather than reusing the
  // cache, which is the whole point of asking for it by hand. The spinner is
  // held for a beat so a cache-warm refresh still reads as an action.
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await getTrades(true);
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  }, [getTrades, isRefreshing]);

  const toggleSort = useCallback((key) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        return prevKey;
      }
      setSortDir('desc');
      return key;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFromDate('');
    setToDate('');
    setPnlFilter('all');
    setSearch('');
  }, []);

  // CSV Export Function for Tax Purposes
  const exportToCSV = () => {
    // Filter only closed trades from filteredTrades
    const closedTrades = filteredTrades.filter(trade => !trade.is_open);
    
    if (closedTrades.length === 0) {
      toast.error('No closed trades to export');
      return;
    }

    // CSV Headers - Tax-friendly format
    const headers = [
      'Trade ID',
      'Close Date',
      'Close Time',
      'Trading Pair',
      'Trade Type',
      'Order Type',
      'Entry Price (USD)',
      'Exit Price (USD)',
      'Leverage',
      'Margin (USD)',
      'Position Size (USD)',
      'Net Profit/Loss (USD)',
      'Return (%)',
      'Trade Result'
    ];

    // Helper function to normalize timestamp
    const normalizeTimestampForExport = (timestamp) => {
      if (!timestamp) return null;
      
      let numTimestamp;
      if (typeof timestamp === 'string') {
        numTimestamp = parseInt(timestamp.trim());
        if (isNaN(numTimestamp)) return null;
      } else if (typeof timestamp === 'number') {
        numTimestamp = timestamp;
      } else {
        return null;
      }

      if (numTimestamp.toString().length >= 19) {
        return Math.floor(numTimestamp / 1000000);
      } else if (numTimestamp > 1e12) {
        return numTimestamp;
      } else {
        return numTimestamp * 1000;
      }
    };

    // Helper to format date for CSV
    const formatDateForCSV = (timestamp) => {
      if (!timestamp) return { date: '', time: '' };
      
      const ts = normalizeTimestampForExport(timestamp);
      if (!ts) return { date: '', time: '' };
      
      const date = new Date(ts);
      if (isNaN(date.getTime())) return { date: '', time: '' };
      
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      return { date: dateStr, time: timeStr };
    };

    // Helper to calculate PnL
    const calculatePnL = (trade) => {
      try {
        const openPrice = parseFloat(trade.open_price);
        const closePrice = parseFloat(trade.trade_info?.closed_price || trade.close_price);
        const collateralAmount = parseFloat(trade.collateral_amount) / 1000000;
        const leverage = parseFloat(trade.leverage);

        if (!openPrice || !closePrice || !collateralAmount || !leverage || openPrice === 0) {
          return { pnl: 0, percentage: 0, isProfit: false };
        }

        const positionSize = collateralAmount * leverage;
        const assetQuantity = positionSize / openPrice;
        
        let pnlUsd;
        if (trade.long) {
          pnlUsd = (closePrice - openPrice) * assetQuantity;
        } else {
          pnlUsd = (openPrice - closePrice) * assetQuantity;
        }

        const pnlPercentage = (pnlUsd / collateralAmount) * 100;
        
        return {
          pnl: pnlUsd,
          percentage: pnlPercentage,
          isProfit: pnlUsd > 0
        };
      } catch (error) {
        console.error('Error calculating PnL for CSV:', error);
        return { pnl: 0, percentage: 0, isProfit: false };
      }
    };

    // Helper to get trade index number
    const getTradeIndexNumber = (userTradeIndex) => {
      if (!userTradeIndex) return "N/A";
      const match = userTradeIndex.toString().match(/\((\d+)\)/);
      return match ? match[1] : userTradeIndex;
    };

    // Convert trades to CSV rows
    const rows = closedTrades.map(trade => {
      const closeDateTime = formatDateForCSV(trade.trade_info?.last_oi_update_ts || trade.close_timestamp);
      const pnlData = calculatePnL(trade);
      const market = getMarketName(trade.market_index);
      const tradeIndex = getTradeIndexNumber(trade.user_trade_index);
      
      const collateralUSD = (parseFloat(trade.collateral_amount) / 1000000).toFixed(2);
      const positionSizeUSD = (parseFloat(trade.collateral_amount) / 1000000 * parseFloat(trade.leverage)).toFixed(2);
      
      return [
        tradeIndex,
        closeDateTime.date,
        closeDateTime.time,
        market,
        trade.long ? 'LONG (BUY)' : 'SHORT (SELL)',
        trade.trade_type === 'limit' ? 'LIMIT' : 'MARKET',
        parseFloat(trade.open_price).toFixed(6),
        parseFloat(trade.trade_info?.closed_price || trade.close_price).toFixed(6),
        parseFloat(trade.leverage).toFixed(2),
        collateralUSD,
        positionSizeUSD,
        pnlData.pnl.toFixed(2),
        pnlData.percentage.toFixed(2),
        pnlData.isProfit ? 'PROFIT' : 'LOSS'
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape cells that contain commas or quotes
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Add BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with date range if applicable
      let filename = 'closed_trades_';
      if (fromDate && toDate) {
        filename += `${fromDate}_to_${toDate}`;
      } else if (fromDate) {
        filename += `from_${fromDate}`;
      } else if (toDate) {
        filename += `until_${toDate}`;
      } else {
        filename += 'all';
      }
      filename += '.csv';
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Show success message
      toast.success(`Successfully exported ${closedTrades.length} ${closedTrades.length === 1 ? 'trade' : 'trades'} to CSV`);
    }
  };


  // Count trades by type for badges
  const tradeCounts = {
    open: trades.filter(t => t.is_open && t.trade_type === 'trade').length,
    closed: trades.filter(t => !t.is_open).length,
    orders: trades.filter(t => t.is_open && (t.trade_type === 'limit' || t.trade_type === 'stop')).length,
  };

  const tabs = [
    { id: 0, label: 'Open', count: tradeCounts.open },
    { id: 1, label: 'Orders', count: tradeCounts.orders },
    { id: 2, label: 'Closed', count: tradeCounts.closed },
  ];

  /**
   * Column geometry per tab.
   *
   * The Open tab shares `TRADES_GRID_COLUMNS` with TradesRow — the header used
   * to declare eleven equal fractions at a 900px floor while the row declared
   * per-column minimums at 780px, so every label sat a few pixels off its own
   * values. Orders and History still use equal fractions, matching their rows.
   */
  const tabColumns = {
    0: {
      grid: TRADES_GRID_COLUMNS,
      minWidth: TRADES_MIN_WIDTH,
      padding: TRADES_ROW_PADDING_X,
      cells: [
        { label: 'Index' },
        { label: 'Date', sort: 'date' },
        { label: 'Pair' },
        { label: 'Type' },
        { label: 'Entry Price' },
        { label: 'Leverage' },
        { label: 'Amount' },
        { label: 'Size', sort: 'size' },
        { label: 'TP/SL' },
        { label: 'PnL' },
        { label: 'Actions' },
      ],
    },
    1: {
      grid: 'repeat(9, minmax(0, 1fr))',
      minWidth: 900,
      padding: 'px-3 sm:px-5',
      cells: [
        { label: 'Index' },
        { label: 'Date', sort: 'date' },
        { label: 'Type' },
        { label: 'Pair' },
        { label: 'Amount' },
        { label: 'Size', sort: 'size' },
        { label: 'Leverage' },
        { label: 'Price' },
        { label: 'Actions' },
      ],
    },
    2: {
      grid: 'repeat(10, minmax(0, 1fr))',
      minWidth: 900,
      padding: 'px-3 sm:px-5',
      cells: [
        { label: 'Index' },
        { label: 'Date', sort: 'date' },
        { label: 'Pair' },
        { label: 'Type' },
        { label: 'Entry Price' },
        { label: 'Leverage' },
        { label: 'Amount' },
        { label: 'Size', sort: 'size' },
        { label: 'Exit Price' },
        { label: 'PnL' },
      ],
    },
  };

  const layout = tabColumns[list] || tabColumns[0];

  // Every narrowing the user has applied, as one dismissible list. Filters that
  // live behind a modal are easy to forget about and then read as "I have no
  // trades" — the chips keep them in view next to the empty state they caused.
  const activeFilters = [
    search.trim() && { key: 'search', label: `"${search.trim()}"`, clear: () => setSearch('') },
    fromDate && { key: 'from', label: `From ${fromDate}`, clear: () => setFromDate('') },
    toDate && { key: 'to', label: `To ${toDate}`, clear: () => setToDate('') },
    pnlFilter !== 'all' && {
      key: 'pnl',
      label: pnlFilter === 'profit' ? 'Profits only' : 'Losses only',
      clear: () => setPnlFilter('all'),
    },
  ].filter(Boolean);

  const modalFilterCount = [fromDate ? 1 : 0, toDate ? 1 : 0, pnlFilter !== 'all' ? 1 : 0]
    .reduce((a, b) => a + b, 0);

  const closedInView = filteredTrades.filter(t => !t.is_open).length;

  // ─── Render helpers ───

  const SortArrow = ({ active }) => (
    <svg
      width="9"
      height="9"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      style={{
        opacity: active ? 1 : 0.35,
        transform: active && sortDir === 'asc' ? 'rotate(180deg)' : 'none',
        transition: 'transform .18s ease, opacity .18s ease',
      }}
    >
      <path d="M6 8.5L2.5 4.5h7L6 8.5z" fill="currentColor" />
    </svg>
  );

  const renderTradeRows = () => {
    if (isLoading) {
      // A skeleton per tab, at the tab's own column count — the rows land in
      // the space the slots already hold, so nothing reflows on arrival.
      const cols = layout.cells.length;
      return (
        <>
          {/* Desktop: skeleton in the table's own grid */}
          <div className="hidden md:flex w-full flex-col">
            {Array.from({ length: 6 }).map((_, row) => (
              <div
                key={row}
                className="w-full grid gap-2 items-center px-2"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, height: 56 }}
              >
                {Array.from({ length: cols }).map((__, col) => (
                  <div key={col} className="flex justify-center">
                    <SkeletonLine w={col === 0 ? 28 : col === cols - 1 ? 20 : "72%"} h={14} index={row} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Mobile: skeleton cards at the height the real cards occupy */}
          <div className="md:hidden flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, row) => (
              <div
                key={row}
                className="px-3.5 py-3"
                style={{
                  background: 'var(--surface)',
                  borderRadius: 'var(--r-panel)',
                  boxShadow: 'var(--pillow-sm)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <SkeletonLine w={110} h={14} index={row} />
                  <SkeletonLine w={62} h={14} index={row} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[0, 1, 2].map((c) => (
                    <SkeletonLine key={c} w="80%" h={12} index={row} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (filteredTrades.length === 0) {
      const emptyCopy = {
        0: { title: 'No open positions', body: 'Positions you open will appear here in real time.' },
        1: { title: 'No pending orders', body: 'Limit and stop orders waiting to fill show up here.' },
        2: { title: 'No closed trades', body: 'Your trade history builds up as positions close.' },
      };
      const narrowed = activeFilters.length > 0;
      const copy = trades.length === 0
        ? { title: 'No trades yet', body: 'Open your first position to start building a history.' }
        : narrowed
          ? { title: 'Nothing matches these filters', body: 'Clear a filter or widen the date range to see more.' }
          : emptyCopy[list] || emptyCopy[0];

      return (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
          <div
            className="w-14 h-14 flex items-center justify-center mb-4"
            style={{
              background: 'var(--surface-2)',
              borderRadius: 'var(--r-panel)',
              boxShadow: 'var(--inset-sm)',
              color: 'var(--ink-4)',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              {narrowed && trades.length > 0 ? (
                <>
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.6-3.6" />
                </>
              ) : (
                <>
                  <path d="M3 17l6-6 4 4 7-7" />
                  <path d="M14 8h6v6" />
                </>
              )}
            </svg>
          </div>
          <p className="text-[15px] font-semibold mb-1.5" style={{ color: 'var(--ink)', fontFamily: 'General Sans' }}>
            {copy.title}
          </p>
          <p className="text-[13px] max-w-[320px] leading-relaxed" style={{ color: 'var(--ink-3)' }}>
            {copy.body}
          </p>
          {narrowed && trades.length > 0 ? (
            <button
              type="button"
              onClick={clearFilters}
              className="ce-btn ce-btn-surface text-sm mt-5"
              style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
            >
              Clear filters
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/perps/markets')}
              className="ce-btn ce-btn-accent text-sm mt-5"
              style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
            >
              Explore markets
            </button>
          )}
        </div>
      );
    }

    return filteredTrades.map((trade) => {
      // Determine which row component to use
      // Check if this trade should be highlighted
      const isHighlighted = highlightedTradeId === trade.user_trade_index;
      const isOrder = (trade.trade_type === 'limit' || trade.trade_type === 'stop') && trade.is_open;

      const Row = isOrder ? OrdersRow : trade.is_open ? TradesRow : HistoryRow;
      const extraProps = isOrder
        ? {}
        : trade.is_open
          ? { showPnlInfo: true }
          : { showShareButton: true, showPnlInfo: true };

      return (
        <div
          key={trade.user_trade_index}
          ref={(el) => {
            if (el) tradeRowRefs.current[trade.user_trade_index] = el;
          }}
          className={isHighlighted ? 'rounded-[16px] transition-all duration-300' : ''}
          style={isHighlighted ? { background: 'var(--cat-accent-soft)' } : undefined}
        >
          <Row trade={trade} onTradeClick={handleTradeClick} {...extraProps} />
        </div>
      );
    });
  };

  return (
    <div
      className="w-full"
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--r-card)',
        padding: '12px 12px 14px',
        boxShadow: 'var(--card)',
      }}
    >
      {/* ─── Toolbar ───
          Tabs on the left, the tools that act on whatever tab is showing on the
          right. Search and refresh used to be missing entirely and export was
          buried two levels inside the filter modal. */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {/* Tabs — a sunken rail holding one raised thumb, matching the trading
            page's table. The 2px purple underline was the 2022 tell. */}
        <div className="ce-seg w-full sm:w-fit" style={{ height: 40 }} role="tablist" aria-label="Trade list">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={list === tab.id}
              onClick={() => setList(tab.id)}
              className="ce-seg-item flex-1 sm:flex-none"
              style={{
                padding: '0 14px',
                fontFamily: 'General Sans',
                ...(list === tab.id
                  ? { background: 'var(--raise)', boxShadow: 'var(--pillow-sm)', color: 'var(--cat-accent)' }
                  : null),
              }}
            >
              {tab.label}
              <span
                className="ce-chip"
                style={{
                  background: 'var(--surface-2)',
                  boxShadow: 'var(--inset-sm)',
                  color: 'var(--ink-2)',
                  fontSize: '11px',
                }}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:flex-none">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              style={{ color: 'var(--ink-4)' }}
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.6-3.6" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search market or ID"
              aria-label="Search trades by market or trade ID"
              className="ce-field w-full sm:w-[196px] text-sm"
              style={{
                height: 40,
                paddingLeft: 32,
                paddingRight: search ? 30 : 12,
                color: 'var(--ink)',
                fontFamily: 'General Sans',
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full"
                style={{ color: 'var(--ink-3)' }}
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" />
                </svg>
              </button>
            )}
          </div>

          {/* Filter */}
          <button
            type="button"
            onClick={() => setIsFilterModalOpen(true)}
            aria-label="Filter trades"
            className={`ce-btn text-sm shrink-0 ${modalFilterCount ? 'ce-btn-accent' : 'ce-btn-surface'}`}
            style={{ height: 40, paddingLeft: 14, paddingRight: 14 }}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="hidden lg:inline font-medium">Filter</span>
            {modalFilterCount > 0 && (
              <span
                className="text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
                style={{ background: 'rgba(255,255,255,.22)', color: '#FFFFFF' }}
              >
                {modalFilterCount}
              </span>
            )}
          </button>

          {/* Export — only meaningful on the Closed tab, so it only exists there */}
          {list === 2 && (
            <button
              type="button"
              onClick={exportToCSV}
              disabled={closedInView === 0}
              aria-label="Export closed trades to CSV"
              title="Export closed trades to CSV"
              className="ce-btn ce-btn-surface text-sm shrink-0"
              style={{ height: 40, paddingLeft: 14, paddingRight: 14 }}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden lg:inline font-medium">Export</span>
            </button>
          )}

          {/* Refresh */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh trades"
            title="Refresh trades"
            className="ce-btn ce-btn-surface shrink-0"
            style={{ height: 40, width: 40, padding: 0 }}
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
              className={isRefreshing ? 'h-4 w-4 ce-spinning' : 'h-4 w-4'}
            >
              <path d="M20 11a8 8 0 10-2.3 6.3" />
              <path d="M20 5v6h-6" />
            </svg>
          </button>
        </div>
      </div>

      {/* ─── Active filter chips + mobile sort ─── */}
      {(activeFilters.length > 0 || filteredTrades.length > 0) && (
        <div className={`flex items-center gap-2 flex-wrap mt-3 ${activeFilters.length === 0 ? 'md:hidden' : ''}`}>
          {activeFilters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={f.clear}
              className="ce-chip"
              style={{
                background: 'var(--cat-accent-soft)',
                color: 'var(--cat-accent)',
                letterSpacing: 0,
                height: 26,
                paddingLeft: 10,
                paddingRight: 8,
                gap: 6,
                cursor: 'pointer',
              }}
              aria-label={`Remove filter ${f.label}`}
            >
              {f.label}
              <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" />
              </svg>
            </button>
          ))}
          {activeFilters.length > 1 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[11.5px] font-medium underline underline-offset-2"
              style={{ color: 'var(--ink-3)' }}
            >
              Clear all
            </button>
          )}

          {/* On mobile the column headers are gone, so sorting needs its own
              control rather than living on a header cell nobody can see. */}
          {filteredTrades.length > 0 && (
            <div className="md:hidden ml-auto flex items-center gap-1.5">
              <span className="ce-eyebrow">Sort</span>
              <button
                type="button"
                onClick={() => toggleSort('date')}
                className="ce-chip"
                style={{
                  height: 26,
                  gap: 5,
                  cursor: 'pointer',
                  letterSpacing: 0,
                  ...(sortKey === 'date'
                    ? { background: 'var(--cat-accent-soft)', color: 'var(--cat-accent)' }
                    : { background: 'var(--surface-2)', boxShadow: 'var(--inset-sm)', color: 'var(--ink-3)' }),
                }}
              >
                Date <SortArrow active={sortKey === 'date'} />
              </button>
              <button
                type="button"
                onClick={() => toggleSort('size')}
                className="ce-chip"
                style={{
                  height: 26,
                  gap: 5,
                  cursor: 'pointer',
                  letterSpacing: 0,
                  ...(sortKey === 'size'
                    ? { background: 'var(--cat-accent-soft)', color: 'var(--cat-accent)' }
                    : { background: 'var(--surface-2)', boxShadow: 'var(--inset-sm)', color: 'var(--ink-3)' }),
                }}
              >
                Size <SortArrow active={sortKey === 'size'} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Date Filter Modal */}
      <Modal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        center
        closeOnOverlayClick={true}
        closeOnEsc={true}
        classNames={{
          modal:
            "min-w-[400px] max-w-[500px] max-sm:min-w-[95vw] max-sm:max-w-[95vw]",
        }}
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-6" style={{ color: "var(--ink)" }}>Filter Trades</h2>

          <div className="space-y-6">
            {/* Date Range Filters */}
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ color: "var(--ink-2)" }}>Date Range</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="modalFromDate" className="block text-sm font-medium mb-2" style={{ color: "var(--ink-2)" }}>
                    From Date
                  </label>
                  <DateField
                    id="modalFromDate"
                    value={fromDate}
                    onChange={setFromDate}
                    max={toDate || undefined}
                  />
                </div>

                <div>
                  <label htmlFor="modalToDate" className="block text-sm font-medium mb-2" style={{ color: "var(--ink-2)" }}>
                    To Date
                  </label>
                  <DateField
                    id="modalToDate"
                    value={toDate}
                    onChange={setToDate}
                    min={fromDate || undefined}
                  />
                </div>
              </div>

              {/* Presets — most date filtering is "the last month", not two
                  calendar pickers. */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {[
                  { label: '7 days', days: 7 },
                  { label: '30 days', days: 30 },
                  { label: '90 days', days: 90 },
                  { label: 'This year', days: null },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      const iso = (d) => d.toISOString().slice(0, 10);
                      const start = preset.days
                        ? new Date(now.getTime() - preset.days * 86400000)
                        : new Date(now.getFullYear(), 0, 1);
                      setFromDate(iso(start));
                      setToDate(iso(now));
                    }}
                    className="ce-chip"
                    style={{
                      height: 28,
                      cursor: 'pointer',
                      letterSpacing: 0,
                      background: 'var(--surface-2)',
                      boxShadow: 'var(--inset-sm)',
                      color: 'var(--ink-2)',
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Profit/Loss Filter */}
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ color: "var(--ink-2)" }}>Profit / Loss</h3>
              <p className="text-xs mb-3" style={{ color: "var(--ink-3)" }}>
                Applies to closed trades — choosing one switches you to the Closed tab.
              </p>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="pnlFilter"
                    value="all"
                    checked={pnlFilter === 'all'}
                    onChange={(e) => setPnlFilter(e.target.value)}
                    className="w-4 h-4"
                    style={{ accentColor: "var(--cat-accent)" }}
                  />
                  <span className="text-sm" style={{ color: "var(--ink-2)" }}>All Trades</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="pnlFilter"
                    value="profit"
                    checked={pnlFilter === 'profit'}
                    onChange={(e) => setPnlFilter(e.target.value)}
                    className="w-4 h-4"
                    style={{ accentColor: "var(--cat-accent)" }}
                  />
                  <span className="text-sm font-medium" style={{ color: "var(--up)" }}>Only Profits</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="pnlFilter"
                    value="loss"
                    checked={pnlFilter === 'loss'}
                    onChange={(e) => setPnlFilter(e.target.value)}
                    className="w-4 h-4"
                    style={{ accentColor: "var(--cat-accent)" }}
                  />
                  <span className="text-sm font-medium" style={{ color: "var(--down)" }}>Only Losses</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-7">
            {activeFilters.length > 0 && (
              <button
                onClick={clearFilters}
                className="ce-btn ce-btn-surface flex-1 text-sm"
                style={{ height: 46, borderRadius: "var(--r-inner)" }}
              >
                Clear All
              </button>
            )}
            <PurpleButton
              text="Apply Filter"
              onClick={() => setIsFilterModalOpen(false)}
              className={activeFilters.length > 0 ? 'flex-1' : 'w-full'}
            />
          </div>
        </div>
      </Modal>

      {/* ─── Table ───
          Horizontal scrolling is a desktop affordance here: on a phone the rows
          render as cards instead, so the 900px floor (and the scrollbar that
          came with it) only applies from `md` up. */}
      <div className="ce-trades-scroll mt-3">
        <div className="ce-trades-body" style={{ '--tbl-min': `${layout.minWidth}px` }}>
          {/* Table Header — desktop only, and sortable where the data allows it.
              A table header is something you read past, not a coloured band: a
              recessed well in the neutral ramp with small-caps labels, same as
              the trading page's tables. */}
          <div
            className={`hidden md:grid w-full whitespace-nowrap ${layout.padding}`}
            role="row"
            style={{
              gridTemplateColumns: layout.grid,
              minWidth: layout.minWidth,
              height: "34px",
              borderRadius: "11px",
              background: "var(--surface-2)",
              boxShadow: "var(--inset-sm)",
              fontSize: "10.5px",
              fontFamily: "General Sans",
              fontWeight: 600,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              color: "var(--ink-3)",
            }}
          >
            {layout.cells.map((cell) => (
              cell.sort ? (
                <button
                  key={cell.label}
                  type="button"
                  onClick={() => toggleSort(cell.sort)}
                  aria-label={`Sort by ${cell.label}`}
                  className="flex items-center justify-center gap-1 transition-colors"
                  style={{
                    font: 'inherit',
                    letterSpacing: 'inherit',
                    textTransform: 'inherit',
                    color: sortKey === cell.sort ? 'var(--cat-accent)' : 'inherit',
                  }}
                >
                  {cell.label}
                  <SortArrow active={sortKey === cell.sort} />
                </button>
              ) : (
                <div key={cell.label} className="flex items-center justify-center">
                  {cell.label}
                </div>
              )
            ))}
          </div>

          {/* Table Body */}
          <div className="mt-1.5 md:mt-2">
            {renderTradeRows()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllTradesTable;
